<template>
  <div class="tr-report-preview">
    <div class="tr-report-preview__header">
      <div>
        <h1 class="tr-report-preview__title">{{ report.report_type.name }}</h1>
      </div>
      <div>
        <img
          src="/images/logos/trendsreport-no-text-logo.svg"
          alt="Trendsreport Logo"
          class="tr-report-preview__logo"
        />
      </div>
    </div>
    <div
      class="file-selection-bar with-report-date file-selection-bar_two-col"
      style="clear: left"
    >
      <div class="report-title">
        <form class="noFlex disabled-dropdown">
          <select name="report-date" id="report-date">
            <option :value="report.id">
              {{ momentFormat(report.issue, 'MMMM YYYY') }}
            </option>
          </select>
          <div class="blocker" />
        </form>
      </div>
      <div class="tr-report-component">
        <div
          class="tr-report-component__icon"
          :class="hasNotifications ? 'active' : 'inactive'"
        >
          <BaseTooltip
            :fromTrendsReport="true"
            placement="top"
            xAlign="center"
            yAlign="top"
            contentWidth="fit-content"
            :fromArticle="true"
            class="tr-report-component__tooltip"
            tooltipColor="white"
          >
            <template #display>
              <span
                class="icon-notification"
                :class="hasNotifications ? 'active' : 'inactive'"
              ></span>
            </template>
            <template #tooltip>
              <p>
                {{
                  hasNotifications
                    ? 'Turn Off Notifications'
                    : 'Receive Notifications'
                }}
              </p>
            </template>
          </BaseTooltip>
        </div>
        <div
          class="tr-report-component__icon"
          :class="isFavorite ? 'active' : 'inactive'"
        >
          <BaseTooltip
            :fromTrendsReport="true"
            placement="top"
            xAlign="center"
            yAlign="top"
            contentWidth="fit-content"
            :fromArticle="true"
            class="tr-report-component__tooltip"
            tooltipColor="white"
          >
            <template #display>
              <span
                class="icon-star"
                :class="isFavorite ? 'active' : 'inactive'"
              ></span>
            </template>
            <template #tooltip>
              <p>
                {{ isFavorite ? 'Remove from Favorites' : 'Add to Favorites' }}
              </p>
            </template>
          </BaseTooltip>
        </div>
        <div
          class="tr-report-component__icon-old"
          @mouseover="pdfHover = true"
          @mouseleave="pdfHover = false"
        >
          <a class="tr-report-component__tooltip" @click="buildPdf()">
            <img
              :src="
                pdfHover
                  ? '/images/icons/icon-pdf.svg'
                  : '/images/icons/icon-pdf-new.svg'
              "
              alt="File"
            />
          </a>
        </div>
        <div
          class="tr-report-component__icon-old"
          v-if="report.data_attachment"
          @mouseover="xlsHover = true"
          @mouseleave="xlsHover = false"
        >
          <a
            class="tr-report-component__tooltip"
            :href="'/storage/' + report.data_attachment"
            download
          >
            <img
              :src="
                xlsHover
                  ? '/images/icons/icon-xls.svg'
                  : '/images/icons/icon-xls-new.svg'
              "
              alt="File"
            />
          </a>
        </div>
      </div>
    </div>
    <div class="row" :class="doscodeDefinition ? 'split-flex' : ''">
      <div class="col">
        <p
          class="small px-1 text-medium-left text-small-center report-upload-date-insider"
        >
          Published {{ getDisplayDate }}
        </p>
      </div>
      <div class="col">
        <p
          class="small px-1 text-medium-right text-small-center report-upload-date-insider"
        >
          {{ doscodeDefinition }}
        </p>
      </div>
      <div
        v-if="report.cwa_data && report.cwa_data.DOSCodeDefinition"
        class="tr-custom-col"
      >
        <BaseTooltip
          :fromTrendsReport="true"
          placement="top"
          xAlign="center"
          yAlign="top"
          contentWidth="medium"
          arrow
          class="tr-report-component__tooltip tr-report-component__tooltip--tr-info"
          tooltipColor="white"
        >
          <template #display>
            <IconInformationCircle color="grey" width="18" height="18" />
          </template>
          <template #tooltip>
            <p>{{ report.cwa_data.DOSCodeDefinition }}</p>
          </template>
        </BaseTooltip>
      </div>
    </div>
    <div>
      <component
        :is="componentMap[template.slug]"
        :cwaData="report.cwa_data"
        :fromTr="true"
        :report="report"
        :imagespath="app_url"
      />
    </div>
  </div>
</template>

<script>
  import BaseTooltip from '~/components/base/BaseTooltip.vue';
  import IconInformationCircle from '~/components/icon/IconInformation.vue';
  import moment from 'moment';
  import { defineAsyncComponent } from 'vue';

  export default {
    components: {
      BaseTooltip,
      IconInformationCircle,
    },
    props: {
      report: {
        type: Object,
        required: true,
      },
      template: {
        type: Object,
        required: true,
      },
      filename: {
        type: String,
        default: 'preview-tr-report',
      },
      app_url: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        pdfHover: false,
        isFavorite: false,
        hasNotifications: false,
        xlsHover: false,
        componentMap: {
          'tr-indicator-type-a': defineAsyncComponent(
            () =>
              import(
                '~/components/trendsreport/html/IndicatorTypeBTemplate.vue'
              )
          ),
          'tr-indicator-type-b': defineAsyncComponent(
            () =>
              import(
                '~/components/trendsreport/html/IndicatorTypeBTemplate.vue'
              )
          ),
          'tr-at-a-glance': defineAsyncComponent(
            () => import('~/components/trendsreport/html/AtAGlanceTemplate.vue')
          ),
          'tr-trends-10': defineAsyncComponent(
            () => import('~/components/trendsreport/html/Trends10Template.vue')
          ),
          'tr-leading-indicator': defineAsyncComponent(
            () =>
              import(
                '~/components/trendsreport/html/LeadingIndicatorTemplate.vue'
              )
          ),
          'tr-economic-outlook': defineAsyncComponent(
            () =>
              import(
                '~/components/trendsreport/html/EconomicOutlookTemplate.vue'
              )
          ),
          'tr-executive-summary': defineAsyncComponent(
            () =>
              import(
                '~/components/trendsreport/html/ExecutiveSummaryTemplate.vue'
              )
          ),
          'tr-global-at-a-glance': defineAsyncComponent(
            () =>
              import(
                '~/components/trendsreport/html/GlobalAtAGlanceTemplate.vue'
              )
          ),
        },
      };
    },
    computed: {
      getDisplayDate() {
        return this.momentFormat(this.report.published_date, 'MMMM DD, YYYY');
      },
      doscodeDefinition() {
        if (!this.report.cwa_data || !this.report.cwa_data.DOSCodeDefinition) {
          return '';
        }
        const definition = this.report.cwa_data.DOSCodeDefinition.trim();
        const definitionArray = definition.split('.');
        let finalSentence = definitionArray.pop() || definitionArray.pop();
        return finalSentence || '';
      },
    },
    methods: {
      momentFormat(date, format) {
        return moment(date).format(format);
      },
      buildPdf() {
        if (this.report.cwa_data) {
          event.preventDefault();
          axios({
            url: `/trendsreport/tr-pdf/${this.report.id}`,
            method: 'GET',
            responseType: 'blob', // Indicate that the response data should be treated as a Blob
          })
            .then((response) => {
              const name = this.filename.replaceAll(' ', '_');
              const date = this.report.issue
                ? new Date(this.report.issue)
                : this.report.publish_date
                  ? new Date(this.report.publish_date)
                  : new Date(this.report.created_at);
              const year = date.getFullYear();
              const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed, add 1
              const day = date.getDate().toString().padStart(2, '0');
              const formattedDate = `${year}-${month}-${day}`;

              const fileName = `${name}-${formattedDate}.pdf`;

              const blob = new Blob([response.data], {
                type: 'application/pdf',
              });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', fileName);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
              // TODO: HANDLE ERROR - This is just silencing the error
            });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .tr-report-preview {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
      @include screen-lg {
        margin-bottom: 4rem;
      }
    }
    &__title {
      font-size: 2rem;
      font-weight: 700;
      text-transform: capitalize;
      margin-right: 10px;
    }
    &__logo {
      display: inline;
      height: 48px;
      width: 48px;
    }
    .disabled-dropdown {
      position: relative;
      .blocker {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
    .tr-report-component {
      width: 100%;
      height: 60px;
      display: flex;
      align-items: center;
      @media screen and (min-width: 800px) {
        width: 300px;
      }
      &__icon {
        width: 100%;
        border-left: 2px solid var(--itr-grey);
        text-align: center;
        color: var(--itr-grey-mid);
        height: 100%;
        cursor: pointer;
        &:hover {
          background: var(--itr-grey-mid);
          transition: all 150ms ease;
          .icon-notification,
          .icon-star {
            color: var(--itr-white);
          }
        }
      }
      &__icon.active {
        background: var(--itr-green-mid);
      }
      &__icon:first-child {
        border-left: 0;
        @media screen and (min-width: 800px) {
          border-left: 2px solid var(--itr-grey);
        }
      }
      &__icon-old {
        width: 100%;
        border-left: 2px solid var(--itr-grey);
        text-align: center;
        color: var(--itr-grey-mid);
        height: 100%;
      }
      &__tooltip {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        .icon-notification,
        .icon-star {
          color: var(--itr-grey-mid);
          font-size: 20px;
        }
        .icon-notification.active,
        .icon-star.active {
          color: var(--itr-white);
        }
      }
      @media screen and (min-width: 800px) {
        width: 300px;
      }
    }
    .tr-custom-col {
      padding: 0 15px;
      align-self: flex-start;
    }
  }
</style>
